import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Grid, FormControlLabel, Switch, Popover, Button, Checkbox, TextField, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, MenuItem } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DeleteField from '../../otherComponent/DeleteStep';
import Reportform from '../../Reportform';
import { optionFor, useDetectionTargets } from './shared/detectionTargets';

interface SwitchComponentProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  selectedNodeData: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function SwitchComponent({ values, index, setFieldValue, onSubmit, handleChange, handleBlur, selectedNodeData, onDeleteField, toSentenceCase, fieldTypeSelector, isSettingsOpen, onSettingsOpen, onSettingsClose }: SwitchComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Detectable classes come from the server, so a new one needs no deploy.
  const detectionTargets = useDetectionTargets();
  const [supportFileInstructions, setSupportFileInstructions] = useState(() => {
    const field = values.fields[index];
    if (field.children && Array.isArray(field.children) && field.children.length > 0) {
      return field.children;
    }
    if (field.supportFileInstructions && Array.isArray(field.supportFileInstructions)) {
      return field.supportFileInstructions;
    }
    return [{ title: '', description: '' }];
  });
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-label-popover' : undefined;

  const field = values.fields[index];
  const settings = field.settings || {};

  // Save handler for support file instructions
  const handleSave = () => {
    setFieldValue(`fields.${index}.supportFileInstructions`, supportFileInstructions);
    setFieldValue(`fields.${index}.children`, supportFileInstructions);
    handleClosePopover();
  };

  return (
    <Box sx={{ width: '100%' }}  >
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ flexGrow: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(values.fields[index].question || 'Untitled Field')}
              </Typography>
              <Switch
                checked={!!values.fields[index].answer}
                onChange={(_, checked) => {
                  setFieldValue(`fields.${index}.answer`, checked);
                  // If turning off, also turn off support file requirement
                  if (!checked && settings.isSupportFileRequired) {
                    setFieldValue(`fields.${index}.settings.isSupportFileRequired`, false);
                  }
                }}
                name={`fields.${index}.answer`}
                color="primary"
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                ref={settingsButtonRef}
                sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex', alignItems: 'center' }}
                onClick={handleEditClick}
              >
                <SettingsIcon fontSize="medium" />
              </Box> 
               <Box sx={{ mt: 2 }}>
                <DeleteField
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
               </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
            <Typography variant="h6">Switch Settings</Typography>
            {fieldTypeSelector ? (
              <Box sx={{ minWidth: 120 }}>
                {fieldTypeSelector}
              </Box>
            ) : null}
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Label"
            name={`fields.${index}.question`}
            defaultValue={field.question}
            onChange={handleChange}
            onBlur={handleBlur}
          
          />
          {/* <TextField
            fullWidth
            size="small"
            label="Description"
            name={`fields.${index}.description`}
            defaultValue={field.description}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={1}
            sx={{ mt:-1}}
          /> */}
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.required || false}
                onChange={(event) => setFieldValue(`fields.${index}.settings.required`, event.target.checked)}
              />
            }
            label="Required"
            sx={{ my: 1, display: 'block' }}
          />
            <Grid item xs={12} sx={{ mt: -3 }}>  
            <FormControlLabel
            control={
              <Checkbox
                checked={settings.isSupportFileRequired || false}
                onChange={(event) => {
                  const isChecked = event.target.checked;
                  setFieldValue(`fields.${index}.settings.isSupportFileRequired`, isChecked);
                  // If enabling support file requirement, also enable the main switch
                  if (isChecked && !values.fields[index].answer) {
                    setFieldValue(`fields.${index}.answer`, true);
                  }
                }}
              />
            }
            label="Image Required"
            sx={{ mb: 2, display: 'block' }}
          /></Grid>
          {settings.isSupportFileRequired && (
            <>
              {/* On-device document detection — guided auto-capture that only
                  accepts the expected document and blocks non-matching ones. */}
              <Box sx={{ mt: 1, mb: 2, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!settings.detection}
                      onChange={(event) => {
                        if (event.target.checked) {
                          const first = detectionTargets[0];
                          setFieldValue(`fields.${index}.settings.detection`, {
                            target: first.value,
                            engine: 'zeroshot',
                            captureOnly: true,
                            framing: first.framing,
                            useQuad: first.useQuad,
                            noun: first.noun,
                            decision: { threshold: 0.85, settleMs: 2500, stableFrames: 3 },
                          });
                          setFieldValue(`fields.${index}.settings.cameraFacing`, first.cameraFacing);
                        } else {
                          setFieldValue(`fields.${index}.settings.detection`, null);
                        }
                      }}
                    />
                  }
                  label="Auto-detect document type"
                  sx={{ display: 'block' }}
                />
                {settings.detection && (
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Document type"
                        value={settings.detection.target || detectionTargets[0].value}
                        onChange={(e) => {
                          // The class carries its own guide box, quad flag and
                          // lens, so adding one needs no change here.
                          const picked = optionFor(detectionTargets, e.target.value);
                          setFieldValue(`fields.${index}.settings.detection.target`, picked.value);
                          setFieldValue(`fields.${index}.settings.detection.framing`, picked.framing);
                          setFieldValue(`fields.${index}.settings.detection.useQuad`, picked.useQuad);
                          setFieldValue(`fields.${index}.settings.detection.noun`, picked.noun);
                          setFieldValue(`fields.${index}.settings.cameraFacing`, picked.cameraFacing);
                        }}
                      >
                        {detectionTargets.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Confidence threshold (0-1)"
                        inputProps={{ step: 0.05, min: 0, max: 1 }}
                        value={settings.detection.decision?.threshold ?? 0.85}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setFieldValue(
                            `fields.${index}.settings.detection.decision.threshold`,
                            isNaN(v) ? 0.85 : v,
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        The camera guides the user to frame the document and only
                        captures a matching one. Non-matching documents are blocked.
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Box>

              {/* Support File Instructions Accordion */}
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                Support File Instructions
              </Typography>
              {supportFileInstructions.map((img: { title: unknown; description: unknown; }, idx: number) => (
                <Accordion key={idx} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">Image {typeof idx === 'number' ? idx + 1 : ''}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Title"
                          value={img.title}
                          onChange={e => {
                            if (typeof idx === 'number') {
                              const updated = [...supportFileInstructions];
                              updated[idx].title = e.target.value;
                              setSupportFileInstructions(updated);
                            }
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={img.description}
                          onChange={e => {
                            if (typeof idx === 'number') {
                              const updated = [...supportFileInstructions];
                              updated[idx].description = e.target.value;
                              setSupportFileInstructions(updated);
                            }
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                          <IconButton
                            onClick={() => {
                              if (supportFileInstructions.length > 1 && typeof idx === 'number') {
                                setSupportFileInstructions(
                                  supportFileInstructions.filter((_: unknown, i: number) => i !== idx)
                                );
                              }
                            }}
                            color="error"
                            disabled={supportFileInstructions.length === 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setSupportFileInstructions([...supportFileInstructions, { title: '', description: '' }])}
                  size="small"
                >
                  Add Image Instruction
                </Button>
              </Box>
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.report?.enabled || false}
                onChange={(event) => {
                  const isChecked = event.target.checked;
                  setFieldValue(
                    `fields.${index}.settings.report`,
                    isChecked
                      ? { ...(settings.report || {}), enabled: true }
                      : { enabled: false, uid: null, name: null }
                  );
                }}
              />
            }
            label="Report "
            sx={{ display: 'block' }}
          />
          {settings.report?.enabled && (
            <Box sx={{ mb: 1 }}>
              <Reportform name={`fields.${index}.settings.report`} />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button size="small" onClick={handleClosePopover}>
              Cancel
            </Button>
            <Button type="button" size="small" variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
